/**
 * Dashboard search + filter bar (v1.1 B7).
 *
 * Composed of:
 *   • A real-time search input that matches against the owner name,
 *     request number, region, service type, and every action's text
 *     on the visible items. Comparison normalizes Arabic via
 *     `@shared/keywords.normalizeArabic` so a user typing "موافقه"
 *     (ha) matches stored "موافقة" (ta-marbuta). This is distinct
 *     from the v1.1 B1 flash detector which intentionally uses
 *     LITERAL matching — the two surfaces have different
 *     forgiveness budgets.
 *   • Filter pills below the input: status (one of the v1.1 B2
 *     colors or any), region multi-select, service-type multi-select,
 *     and a "changed within N hours" bucket. Each active filter shows
 *     as a removable chip with an X button; a "Clear all" button on
 *     the trailing edge wipes the whole state.
 *
 * Filter state lives in sessionStorage so a tab refresh keeps the
 * search alive but a fresh tab starts clean. (LocalStorage would
 * cross-pollinate between e.g. the office TV and someone's laptop on
 * the same browser profile — sessionStorage is the right scope.)
 *
 * The component is pure: it owns its state, exposes the current filter
 * via a typed callback, and lets the caller decide how to apply it.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter as FilterIcon, X, Search } from "lucide-react";
import { CANONICAL_REGIONS, CANONICAL_SERVICE_TYPES } from "@/lib/regions";

const SESSION_KEY = "azzam.dashboard.filters";

export type StatusPillFilter = "any" | "مكتمل" | "مرفوض" | "مكلف";
export type TimeBucket = "any" | "1h" | "6h" | "24h" | "72h";

export interface FilterState {
  search: string;
  status: StatusPillFilter;
  regions: string[];
  serviceTypes: string[];
  timeBucket: TimeBucket;
}

const DEFAULT_STATE: FilterState = {
  search: "",
  status: "any",
  regions: [],
  serviceTypes: [],
  timeBucket: "any",
};

interface Props {
  state: FilterState;
  onChange: (state: FilterState) => void;
  resultCount: number;
}

/** Read the persisted state from sessionStorage; returns the default
 *  shape if nothing's there or it parses as garbage. */
export function readPersistedFilters(): FilterState {
  if (typeof sessionStorage === "undefined") return DEFAULT_STATE;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    return {
      search: typeof parsed.search === "string" ? parsed.search : "",
      status: (parsed.status as StatusPillFilter) ?? "any",
      regions: Array.isArray(parsed.regions) ? parsed.regions.filter((r): r is string => typeof r === "string") : [],
      serviceTypes: Array.isArray(parsed.serviceTypes)
        ? parsed.serviceTypes.filter((s): s is string => typeof s === "string")
        : [],
      timeBucket: (parsed.timeBucket as TimeBucket) ?? "any",
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function writePersistedFilters(state: FilterState): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // Quota / private-mode edge — ignore; filters just don't persist.
  }
}

export function DashboardFilters({ state, onChange, resultCount }: Props) {
  const { t } = useTranslation();
  const [panelOpen, setPanelOpen] = useState(false);

  // Sync to sessionStorage on every state change. Caller owns the
  // truth; this is a side-effect mirror.
  useEffect(() => {
    writePersistedFilters(state);
  }, [state]);

  const activeChips = useMemo(() => buildChips(state, t), [state, t]);
  const hasFilters = activeChips.length > 0 || state.search.length > 0;

  const update = (patch: Partial<FilterState>) => onChange({ ...state, ...patch });

  return (
    <div className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search
            className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted dark:text-muted-dark"
            aria-hidden
          />
          <input
            type="search"
            value={state.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder={t("filter.searchPlaceholder")}
            aria-label={t("filter.searchPlaceholder")}
            className="rtl-isolate w-full rounded-md border border-hairline dark:border-hairline-dark bg-canvas dark:bg-canvas-dark ps-8 pe-3 py-2 text-sm text-fg dark:text-fg-dark"
          />
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          aria-expanded={panelOpen}
          aria-label={t("filter.byStatus")}
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-md border border-hairline dark:border-hairline-dark px-3 py-2 text-sm ${
            panelOpen
              ? "bg-pulse/15 text-pulse dark:bg-pulse-dark/20 dark:text-pulse-dark"
              : "text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          }`}
        >
          <FilterIcon className="h-4 w-4" aria-hidden />
          {activeChips.length > 0 ? (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-pulse dark:bg-pulse-dark text-canvas dark:text-canvas-dark text-xs font-bold px-1">
              {activeChips.length}
            </span>
          ) : null}
        </button>
        {hasFilters ? (
          <button
            type="button"
            onClick={() => onChange(DEFAULT_STATE)}
            className="shrink-0 rounded-md px-3 py-2 text-sm text-muted dark:text-muted-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("filter.clearAll")}
          </button>
        ) : null}
      </div>

      {/* Active-chip strip. The X on each chip removes that specific
          predicate without touching the rest of the state. */}
      {activeChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => onChange(chip.remove(state))}
              className="rtl-isolate inline-flex items-center gap-1 rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs text-fg dark:text-fg-dark hover:bg-hairline/60 dark:hover:bg-hairline-dark/80"
            >
              <span>{chip.label}</span>
              <X className="h-3 w-3" aria-hidden />
            </button>
          ))}
          <span className="ms-auto text-xs text-muted dark:text-muted-dark">
            {t("filter.results", { count: resultCount })}
          </span>
        </div>
      ) : state.search.length > 0 ? (
        <div className="text-xs text-muted dark:text-muted-dark">
          {t("filter.results", { count: resultCount })}
        </div>
      ) : null}

      {/* Filter panel — collapsed by default so the dashboard stays
          clean for the common no-filter case. */}
      {panelOpen ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-2 border-t border-hairline dark:border-hairline-dark">
          <FilterSelect
            label={t("filter.byStatus")}
            value={state.status}
            options={[
              { value: "any", label: t("filter.any") },
              { value: "مكتمل", label: t("filter.complete") },
              { value: "مرفوض", label: t("filter.rejected") },
              { value: "مكلف", label: t("filter.assigned") },
            ]}
            onChange={(v) => update({ status: v as StatusPillFilter })}
          />
          <FilterMultiSelect
            label={t("filter.byRegion")}
            options={CANONICAL_REGIONS as readonly string[]}
            selected={state.regions}
            onChange={(regions) => update({ regions })}
          />
          <FilterMultiSelect
            label={t("filter.byServiceType")}
            options={CANONICAL_SERVICE_TYPES as readonly string[]}
            selected={state.serviceTypes}
            onChange={(serviceTypes) => update({ serviceTypes })}
          />
          <FilterSelect
            label={t("filter.byTime")}
            value={state.timeBucket}
            options={[
              { value: "any", label: t("filter.any") },
              { value: "1h", label: t("filter.hours1") },
              { value: "6h", label: t("filter.hours6") },
              { value: "24h", label: t("filter.hours24") },
              { value: "72h", label: t("filter.hours72") },
            ]}
            onChange={(v) => update({ timeBucket: v as TimeBucket })}
          />
        </div>
      ) : null}
    </div>
  );
}

interface ChipDescriptor {
  id: string;
  label: string;
  remove: (state: FilterState) => FilterState;
}

function buildChips(state: FilterState, t: (k: string) => string): ChipDescriptor[] {
  const chips: ChipDescriptor[] = [];
  if (state.status !== "any") {
    const label =
      state.status === "مكتمل"
        ? t("filter.complete")
        : state.status === "مرفوض"
          ? t("filter.rejected")
          : t("filter.assigned");
    chips.push({
      id: `status:${state.status}`,
      label: `${t("filter.byStatus")}: ${label}`,
      remove: (s) => ({ ...s, status: "any" }),
    });
  }
  for (const region of state.regions) {
    chips.push({
      id: `region:${region}`,
      label: `${t("filter.byRegion")}: ${region}`,
      remove: (s) => ({ ...s, regions: s.regions.filter((r) => r !== region) }),
    });
  }
  for (const st of state.serviceTypes) {
    chips.push({
      id: `serviceType:${st}`,
      label: `${t("filter.byServiceType")}: ${st}`,
      remove: (s) => ({ ...s, serviceTypes: s.serviceTypes.filter((x) => x !== st) }),
    });
  }
  if (state.timeBucket !== "any") {
    const bucketLabels: Record<Exclude<TimeBucket, "any">, string> = {
      "1h": t("filter.hours1"),
      "6h": t("filter.hours6"),
      "24h": t("filter.hours24"),
      "72h": t("filter.hours72"),
    };
    chips.push({
      id: `time:${state.timeBucket}`,
      label: `${t("filter.byTime")}: ${bucketLabels[state.timeBucket]}`,
      remove: (s) => ({ ...s, timeBucket: "any" }),
    });
  }
  return chips;
}

interface SelectProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}

function FilterSelect({ label, value, options, onChange }: SelectProps) {
  return (
    <label className="block text-xs">
      <span className="block mb-1 text-muted dark:text-muted-dark">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-hairline dark:border-hairline-dark bg-canvas dark:bg-canvas-dark px-2 py-1.5 text-sm text-fg dark:text-fg-dark"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface MultiProps {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

/** Compact multi-select rendered as a clickable chip list. Native
 *  <select multiple> on a TV viewport is fiddly to operate; clickable
 *  chips are direct-manipulation. The chip cluster is scrollable
 *  vertically when long. */
function FilterMultiSelect({ label, options, selected, onChange }: MultiProps) {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((x) => x !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  return (
    <div className="text-xs">
      <span className="block mb-1 text-muted dark:text-muted-dark">{label}</span>
      <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1 rounded-md border border-hairline dark:border-hairline-dark bg-canvas dark:bg-canvas-dark p-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`rtl-isolate rounded-full px-2 py-0.5 text-xs ${
                on
                  ? "bg-pulse/20 text-pulse dark:bg-pulse-dark/30 dark:text-pulse-dark"
                  : "bg-hairline/40 dark:bg-hairline-dark/60 text-fg dark:text-fg-dark hover:bg-hairline/60 dark:hover:bg-hairline-dark/80"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
